import { useQuery, useMutation, useQueryClient } from '@tanstack/react-query';

import { api } from '@/lib/axios';

const KEYS = {
  all: ['provider-users'] as const,
  list: (providerId: string) => [...KEYS.all, 'list', providerId] as const,
};

export interface ProviderUserItem {
  id: string;
  user: number;
  user_email: string;
  user_first_name: string;
  user_last_name: string;
  user_role: string;
  provider: string;
  provider_role: 'owner' | 'editor';
  invited_by: number | null;
  invited_by_email: string | null;
  created: string;
  updated: string;
}

export function useProviderUsers(providerId: string) {
  return useQuery({
    queryKey: KEYS.list(providerId),
    queryFn: async () => {
      const { data } = await api.get<ProviderUserItem[]>(
        `/api/providers/providers/${providerId}/users/`,
      );
      return data;
    },
    enabled: !!providerId,
  });
}

export function useAddProviderUser(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (payload: { email: string; provider_role: string; invite?: boolean }) => {
      const { data: result } = await api.post(
        `/api/providers/providers/${providerId}/users/`,
        payload,
      );
      return result;
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list(providerId) }),
  });
}

export function useRemoveProviderUser(providerId: string) {
  const qc = useQueryClient();
  return useMutation({
    mutationFn: async (providerUserId: string) => {
      await api.delete(`/api/providers/providers/${providerId}/users/${providerUserId}/`);
    },
    onSuccess: () => qc.invalidateQueries({ queryKey: KEYS.list(providerId) }),
  });
}
